// Shared download helper for LabelTemplateListPage + LabelTemplateEditModal.
// Goes through the shared api client (services/api.ts `getBlob`), which
// keeps the in-memory auth token handling and BASE_URL prefixing consistent
// with every other call site — paths here must NOT include the `/api`
// prefix, `api.getBlob`'s BASE_URL already prepends it.
import { api, ApiError } from '../../services/api'

const DOWNLOAD_EXTENSION: Record<string, string> = {
  dymo_client: 'dymo',
  zebra_browser_print: 'zpl',
}

function slugifyTemplateName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Fetches a label template's raw body and triggers a browser download.
 */
export async function downloadLabelTemplate(
  id: string,
  name: string,
  driver: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  let result: { blob: Blob; filename: string | null }
  try {
    result = await api.getBlob(`/admin/label-templates/${id}/download`)
  } catch (err) {
    return { ok: false, error: err instanceof ApiError ? err.message : 'Download failed' }
  }

  const ext = DOWNLOAD_EXTENSION[driver] ?? 'txt'
  const filename = result.filename ?? `${slugifyTemplateName(name)}.${ext}`

  const objectUrl = URL.createObjectURL(result.blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Synchronous revoke can cancel the download in some browsers; defer it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0)

  return { ok: true }
}
